// src/components/RecepcionDigital/Manifiesto.jsx
//
// ── Decisiones de diseño ──────────────────────────────────────────────────────
// Pestaña Manifiesto: la lista de trabajo viva, NO una sala de espera. La
// ordenación por defecto es "prioridad de operador" (lista_para_producción
// primero, expirada al final — derivada de STATUS_META.priority, definida UNA
// vez en StatusChip), con desempate por actividad más antigua del cliente: lo
// que más tiempo lleva esperando al operador sube (throughput, Principio del
// brief: la prioridad la marca el chip de estado, no el orden cronológico).
// - La pestaña es dueña de sus datos: listDrafts() al montar y re-fetch tras
//   cada acción del drawer. El shell no le pasa datos — el swap al backend real
//   solo toca src/api.
// - Búsqueda inline teléfono-primero (≥3 dígitos → substring sobre los dígitos
//   del teléfono; si no, nombre sin acentos). El Ctrl+K global vive en el shell.
// - Chips de filtro multi-selección con contadores y aria-pressed; los chips de
//   tenant solo aparecen si el operador cubre más de un tenant.
// - Estados explícitos: cargando (skeleton), error con "Reintentar", vacío con
//   calidez (Principios 5 y 6). Grilla 1 columna en tablet, 2 a 1440px.
// ──────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, Search, AlertTriangle } from 'lucide-react';
import { listDrafts, updateDraft, discardDraft, snoozeDraft } from '../../api/mock/recepcionDigital';
import { STATUS_META } from './shared/StatusChip';
import EmptyState from './shared/EmptyState';
import EncomiendaCard from './EncomiendaCard';
import CardDetailDrawer from './CardDetailDrawer';

// Sin acentos y en minúsculas — búsqueda tolerante para nombres como "María".
const normalize = (s) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const digitsOf = (s) => (s || '').replace(/\D/g, '');

const STATUS_ORDER = Object.keys(STATUS_META); // ya ordenadas por prioridad en el archivo

const Manifiesto = ({ user, onOpenDraft }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [query, setQuery] = useState('');
  const [sortMode, setSortMode] = useState('prioridad'); // 'prioridad' | 'recientes'
  const [statusFilters, setStatusFilters] = useState(() => new Set());
  const [tenantFilters, setTenantFilters] = useState(() => new Set());

  const [detailId, setDetailId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState(null);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listDrafts();
      setDrafts(data);
    } catch (err) {
      setLoadError(err.message || 'No se pudieron cargar las encomiendas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  // ── Filtrado + ordenación ──────────────────────────────────────────────────
  const tenants = useMemo(
    () => [...new Set(drafts.map((d) => d.tenant_id).filter(Boolean))],
    [drafts]
  );

  const visibleDrafts = useMemo(() => {
    let list = drafts;

    const qDigits = digitsOf(query);
    if (qDigits.length >= 3) {
      list = list.filter((d) => digitsOf(d.client?.phone).includes(qDigits));
    } else if (query.trim()) {
      const q = normalize(query);
      list = list.filter((d) => normalize(d.client?.name).includes(q));
    }

    if (statusFilters.size > 0) {
      list = list.filter((d) => statusFilters.has(d.status));
    }
    if (tenantFilters.size > 0) {
      list = list.filter((d) => tenantFilters.has(d.tenant_id));
    }

    const byActivityAsc = (a, b) =>
      new Date(a.last_client_activity_at) - new Date(b.last_client_activity_at);

    return [...list].sort((a, b) => {
      if (sortMode === 'recientes') {
        return new Date(b.last_client_activity_at) - new Date(a.last_client_activity_at);
      }
      const pa = STATUS_META[a.status]?.priority ?? 99;
      const pb = STATUS_META[b.status]?.priority ?? 99;
      if (pa !== pb) return pa - pb;
      return byActivityAsc(a, b); // lo que más espera al operador, primero
    });
  }, [drafts, query, statusFilters, tenantFilters, sortMode]);

  const statusCounts = useMemo(() => {
    const counts = {};
    for (const d of drafts) counts[d.status] = (counts[d.status] || 0) + 1;
    return counts;
  }, [drafts]);

  const toggleSetEntry = (setter) => (value) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  const toggleStatus = toggleSetEntry(setStatusFilters);
  const toggleTenant = toggleSetEntry(setTenantFilters);

  // ── Acciones del drawer ────────────────────────────────────────────────────
  const handleAction = async (action, id) => {
    setPendingAction(action);
    setActionError(null);
    try {
      if (action === 'snooze') await snoozeDraft(id);
      else if (action === 'follow_up') await updateDraft(id, { follow_up: true });
      else if (action === 'escalate') await updateDraft(id, { escalated: true });
      else if (action === 'discard') await discardDraft(id);

      if (action === 'discard' || action === 'snooze') setDetailId(null);
      await fetchDrafts();
    } catch (err) {
      setActionError(err.message || 'La acción no se pudo completar. Intente de nuevo.');
    } finally {
      setPendingAction(null);
    }
  };

  const detailDraft = drafts.find((d) => d.id === detailId) || null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Búsqueda */}
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: 'var(--text-tertiary)' }}
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por teléfono o nombre…"
          aria-label="Buscar encomienda por teléfono o nombre"
          className="input w-full pl-9"
          style={{ minHeight: 44 }}
        />
      </div>

      {/* Orden + filtros */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          Ordenar por{' '}
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            aria-label="Modo de ordenación de las tarjetas"
            className="input ml-1 text-xs"
            style={{ minHeight: 36, width: 'auto', display: 'inline-block' }}
          >
            <option value="prioridad">Prioridad de operador</option>
            <option value="recientes">Más recientes</option>
          </select>
        </label>

        <span className="mx-1 h-4 w-px" style={{ background: 'var(--border)' }} aria-hidden="true" />

        {STATUS_ORDER.map((status) => {
          const meta = STATUS_META[status];
          const active = statusFilters.has(status);
          const count = statusCounts[status] || 0;
          return (
            <button
              key={status}
              onClick={() => toggleStatus(status)}
              aria-pressed={active}
              aria-label={`Filtrar por estado ${meta.label} (${count})`}
              className="rounded-md border px-2 py-1 text-[11px] font-medium transition-colors focus-visible:outline focus-visible:outline-2 hover:opacity-90 disabled:opacity-50"
              style={{
                background: active ? meta.bg : 'var(--input-bg)',
                color: active ? meta.color : 'var(--text-secondary)',
                borderColor: active ? meta.border : 'var(--border)',
                outlineColor: 'var(--avianca-red)',
              }}
            >
              {meta.label} · {count}
            </button>
          );
        })}

        {tenants.length > 1 && (
          <>
            <span className="mx-1 h-4 w-px" style={{ background: 'var(--border)' }} aria-hidden="true" />
            {tenants.map((tenantId) => {
              const active = tenantFilters.has(tenantId);
              const count = drafts.filter((d) => d.tenant_id === tenantId).length;
              return (
                <button
                  key={tenantId}
                  onClick={() => toggleTenant(tenantId)}
                  aria-pressed={active}
                  aria-label={`Filtrar por empresa ${tenantId} (${count})`}
                  className="rounded-md border px-2 py-1 text-[11px] font-medium uppercase tracking-wide transition-colors focus-visible:outline focus-visible:outline-2 hover:opacity-90"
                  style={{
                    background: active ? 'var(--nav-active-bg)' : 'var(--input-bg)',
                    color: active ? 'var(--text-main)' : 'var(--text-secondary)',
                    borderColor: active ? 'var(--border-hover)' : 'var(--border)',
                    outlineColor: 'var(--avianca-red)',
                  }}
                >
                  {tenantId} · {count}
                </button>
              );
            })}
          </>
        )}
      </div>

      {/* Contenido */}
      <div className="mt-4">
        {loading && (
          <div
            className="grid grid-cols-1 xl:grid-cols-2 gap-4"
            role="status"
            aria-label="Cargando encomiendas"
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-36 rounded-lg border animate-pulse"
                style={{ background: 'var(--tile)', borderColor: 'var(--border)' }}
              />
            ))}
            <span className="sr-only">Cargando encomiendas…</span>
          </div>
        )}

        {!loading && loadError && (
          <EmptyState
            icon={AlertTriangle}
            title="No se pudieron cargar las encomiendas."
            subtitle={loadError}
            action={
              <button
                onClick={fetchDrafts}
                className="min-h-[44px] px-4 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2"
                style={{ background: 'var(--avianca-red)', outlineColor: 'var(--avianca-red)' }}
              >
                Reintentar
              </button>
            }
          />
        )}

        {!loading && !loadError && drafts.length === 0 && (
          <EmptyState
            icon={Inbox}
            title="Todavía no hay encomiendas pendientes."
            subtitle="Cuando un cliente empiece a chatear con el agente, van a aparecer aquí."
          />
        )}

        {!loading && !loadError && drafts.length > 0 && visibleDrafts.length === 0 && (
          <EmptyState
            icon={Search}
            title="Ninguna encomienda coincide con su búsqueda."
            subtitle="Pruebe con otro teléfono o nombre, o quite los filtros activos."
          />
        )}

        {!loading && !loadError && visibleDrafts.length > 0 && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {visibleDrafts.map((draft) => (
              <EncomiendaCard
                key={draft.id}
                draft={draft}
                onOpen={onOpenDraft}
                onOpenDetail={setDetailId}
              />
            ))}
          </div>
        )}
      </div>

      <CardDetailDrawer
        draft={detailDraft}
        open={Boolean(detailDraft)}
        onClose={() => { setDetailId(null); setActionError(null); }}
        onOpen={(id) => { setDetailId(null); onOpenDraft(id); }}
        onAction={handleAction}
        pendingAction={pendingAction}
        actionError={actionError}
      />
    </div>
  );
};

export default Manifiesto;
